import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronRight, ArrowUpRight } from 'lucide-react';

export default function Portfolio() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: 'LYKKE UMI — Skandinavisk minimalisme ved Lillehammer',
      category: 'Privatbolig',
      image: 'https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/1f6334194_663692458_1682173199798150_473169998624611140_n.jpg',
      description: 'Kompakt badstue med ren geometri og perfekt integrering i nordisk landskap',
      results: [
        'LYKKE UMI med aspetre og naturlig tre',
        'Energieffektiv HUUM Mini ovn 9kW',
        'Familie bruker daglig året rundt — favorittsted for relaxasjon'
      ],
      details: 'LYKKE UMI representerer det beste av skandinavisk design — enkelt, elegantly og tidløst. Plasseringen mellom betulene tillater en magisk overgang fra vinterkulde til badstuevarme. Interiøret av lyst aspetre kontrasterer vakkert med den grå Gran-kledningen. Hver medlem av familien har sitt favorittsted på de toetasjes benken, og det store vinduet gir utsikt til skogen mens man nyter varmen.'
    },
    {
      id: 2,
      title: 'LYKKE DOLOMITI — Resort-klasse wellness i Trøndelag',
      category: 'Kommersiell',
      image: 'https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/7c31bced8_Dolomiti_black_1.jpg',
      description: 'Premium panorama-badstue designet for resort-opplevelse med maksimal lys',
      results: [
        'LYKKE DOLOMITI med sort Kebony-kledning og varmebehandlet innvendig tre',
        'HUUM ovn med smart kontroll og panorama-glass på hele siden',
        'Resort rapporterer 35% økt gjesteboking — badstuen er hovedattraksjon'
      ],
      details: 'LYKKE DOLOMITI er designet som et statement i landskapet. Det sorte utvendige tre med store glassflater skaper en sofistikert kontrast mellom inne og ute. Gjestene sitter ved full varme fra HUUM ovnen og ser direkte ut på Trøndelagsnaturen. Varmebehandlet tre innvendig, LED-belysning og prémiesteinene gjør dette til en luksuriøs spa-opplevelse som gjester vil snakke om lenge etter avreisen.'
    },
    {
      id: 3,
      title: 'LYKKE MORI — Familiesenter på Vestlandet',
      category: 'Privatbolig',
      image: 'https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/351d204a6_LYKKE_UMI_DETALMORI.png',
      description: 'Romslig badstue med to nivåer designet for familiesamvær og løyly-tradisjon',
      results: [
        'LYKKE MORI med to benkenivåer og stor glassdør',
        'HUUM Hive 10kW ovn for kraftig løyly',
        'Familie på 5 bruker hver kveld hele året — blitt deres møtepunkt'
      ],
      details: 'LYKKE MORI er familiens nye hjerte. Det romslige interiøret med to trebenknivåer tillater at hele familien kan sitte sammen. Fra innsiden ser man direkte ut på havet gjennom de store glassvinduene — en perfekt kontrast mellom iskald sjøluft og badstuevarme. Traditionen med løyly er kommet tilbake, og barna har nå sitt eget ritual hver kveld. Aspetre-benken patinerer vakkert over tid, og steninnsatsen under høy oppvarming skaper den autentiske løyly-opplevelsen som gjør LYKKE MORI til familiens favorittsted.'
    },
    {
      id: 4,
      title: 'LYKKE VERBIER — Arkitekturens møte med funksjon i Follo',
      category: 'Kommersiell',
      image: 'https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/53d40a868_LYKKE_DETALMORI1.png',
      description: 'Design-badstue med klassisk skråtak og panorama-glass som integreres i bylandskap',
      results: [
        'LYKKE VERBIER med metalltak og full glassvegg mot vest',
        'HUUM ovn med optimal luftsirkulasjon for maksimal løyly-kraft',
        'Nabo-beboere rapporterer økt trivsel — badstuen er referansepunkt i nabolaget'
      ],
      details: 'LYKKE VERBIER er designet som et arkitektonisk statement. Det klassiske metallskråtaket gir både funksjon og estetikk — vann renner bort, snø glir av, og designen er tidløs. Den store glassvegg mot vest tillater solnedgang-badstuing med utsikt over hele dalen. Sort Kebony-tre på sidene kontrasterer perfekt med glasset og skaper en følelse av modernitet samtidig som det respekterer tradisjonelle badstue-verdier. Mange fra nabolaget kommer nå til kvelden og nyter dampløyly fra plassen utenfor — LYKKE VERBIER har blitt et møtepunkt.'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-32 text-center"
          >
            <p className="font-body text-base tracking-widest uppercase text-[#a6b894] font-semibold mb-4">Referanseprosjekter</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-tight mb-8 text-[#2a1810]">
              Badstuer i Praksis
            </h1>
            <p className="font-serif text-2xl text-foreground/70 leading-relaxed max-w-2xl mx-auto">
              Se hvordan LYKKE badstuer blir sentrale elementer i menneskers liv. Fra nordiske privatbolig-skatter til kommersielle resort som setter nye standarder.
            </p>
          </motion.div>

          {/* Projects Grid */}
          <div className="space-y-12 mb-32">
            {projects.map((project, idx) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="grid md:grid-cols-2 gap-12 items-center border-t border-border/40 pt-12"
              >
                {idx % 2 === 0 ? (
                  <>
                    <div className="order-2 md:order-1">
                      <p className="font-body text-xs tracking-widest uppercase text-[#a6b894]/80 font-semibold mb-2">
                        {project.category}
                      </p>
                      <h2 className="font-heading text-3xl font-light mb-6">{project.title}</h2>
                      <p className="font-serif text-2xl text-foreground/70 leading-relaxed mb-8">
                        {project.description}
                      </p>

                      <div className="space-y-4 mb-8 pt-8 border-t border-border/40">
                        <h3 className="font-heading text-base tracking-widest uppercase text-[#a6b894] font-semibold">Resultater</h3>
                        {project.results.map((result, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <ChevronRight size={16} className="text-[#a6b894] flex-shrink-0 mt-1" />
                            <span className="font-serif text-2xl text-foreground/70">{result}</span>
                          </div>
                        ))}
                        </div>

                        <button
                        onClick={() => setSelectedProject(project.id === selectedProject ? null : project.id)}
                        className="flex items-center gap-2 font-heading text-sm tracking-widest uppercase text-[#a6b894] hover:text-[#a6b894]/60 transition-colors font-semibold"
                        >
                        {selectedProject === project.id ? 'Lukk' : 'Les mer'} <ArrowUpRight size={14} />
                        </button>
                        </div>

                        <div className="order-1 md:order-2 h-96 bg-secondary/20">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover rounded-[6px]"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-96 bg-secondary/20">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover rounded-[6px]"
                      />
                    </div>

                    <div>
                      <p className="font-body text-base tracking-widest uppercase text-[#a6b894]/80 font-semibold mb-2">
                        {project.category}
                      </p>
                      <h2 className="font-heading text-3xl font-light mb-6">{project.title}</h2>
                      <p className="font-serif text-2xl text-foreground/70 leading-relaxed mb-8">
                        {project.description}
                      </p>

                      <div className="space-y-4 mb-8 pt-8 border-t border-border/40">
                        <h3 className="font-heading text-base tracking-widest uppercase text-[#a6b894] font-semibold">Resultater</h3>
                        {project.results.map((result, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <ChevronRight size={16} className="text-[#a6b894] flex-shrink-0 mt-1" />
                              <span className="font-serif text-2xl text-foreground/70">{result}</span>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={() => setSelectedProject(project.id === selectedProject ? null : project.id)}
                          className="flex items-center gap-2 font-heading text-sm tracking-widest uppercase text-[#a6b894] hover:text-[#a6b894]/60 transition-colors font-semibold"
                        >
                          {selectedProject === project.id ? 'Lukk' : 'Les mer'} <ArrowUpRight size={14} />
                        </button>
                        </div>
                        </>
                        )}

                        {selectedProject === project.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="md:col-span-2 pt-8 border-t border-border/30"
                  >
                    <div className="bg-secondary/20 rounded-[6px] p-8">
                      <h4 className="font-heading text-lg font-light mb-4">Prosjektdetaljer</h4>
                      <p className="font-serif text-2xl text-foreground/70 leading-relaxed">
                        {project.details}
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Stats Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-32 bg-[#2a1810] text-background rounded-[6px] p-12 md:p-16"
          >
            <div className="grid md:grid-cols-4 gap-12 text-center">
              <div>
                <p className="font-heading text-4xl font-light mb-2">50+</p>
                <p className="font-serif text-2xl text-background/70">Badstuer installert</p>
              </div>
              <div>
                <p className="font-heading text-4xl font-light mb-2">4.9★</p>
                <p className="font-serif text-2xl text-background/70">Gjennomsnittlig rating</p>
              </div>
              <div>
                <p className="font-heading text-4xl font-light mb-2">98%</p>
                <p className="font-serif text-2xl text-background/70">Fornøyde kunder</p>
              </div>
              <div>
                <p className="font-heading text-4xl font-light mb-2">10 år</p>
                <p className="font-serif text-2xl text-background/70">Garantert kvalitet</p>
              </div>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-20 border-t border-border/40 pt-20 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-6">
              Hva er ditt LYKKE-prosjekt?
            </h2>
            <p className="font-serif text-2xl text-foreground/70 mb-12 max-w-2xl mx-auto">
              La oss hjelpe deg med å designe og installere en LYKKE badstue som blir sentral i ditt liv — skandinavisk håndverk og design møter nordisk natur.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <a
                href="/be-om-tilbud"
                className="px-8 py-3 bg-[#2a1810] text-background font-sans tracking-widest text-sm font-semibold rounded-sm hover:bg-[#2a1810]/90 transition-colors"
              >
                BE OM TILBUD
              </a>
              <a
                href="/sauna-ly"
                className="px-8 py-3 border-2 border-foreground text-foreground font-sans tracking-widest text-sm font-semibold rounded-sm hover:bg-foreground/10 transition-colors"
              >
                UTFORSK MODELLER
              </a>
            </div>
          </motion.section>
        </div>
      </div>

      <Footer />
    </div>
  );
}